<template>
  <v-dialog :model-value="group !== null" max-width="460" @update:model-value="(value: boolean) => !value && emit('close')">
    <div v-if="group" class="rounded-xl bg-white p-5">
      <h3 class="m-0 text-base font-semibold text-gray-900">Edit capacity</h3>
      <p class="m-0 mt-1 text-xs text-gray-400">{{ groupLabel(group) }} · {{ weekLabel }}</p>

      <p v-if="isNonCapacityPo" class="m-0 mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        {{ group.productCategory }} PO — planned into this week without consuming capacity, so it has no quantity to edit.
      </p>

      <template v-else>
        <div class="mt-3 space-y-2">
          <div v-for="portion in group.portions" :key="portion.assignmentId" class="flex items-center gap-3">
            <span class="w-12 shrink-0 text-xs font-semibold text-gray-700">{{ portion.typeCode }}</span>
            <v-text-field
              v-model.number="draft[portion.assignmentId]"
              type="number"
              min="0"
              variant="outlined"
              density="compact"
              hide-details
              :aria-label="`${portion.typeCode} quantity`"
            />
            <span class="w-32 shrink-0 text-right text-[11px]" :class="isOverbooked(portion) ? 'font-semibold text-red-600' : 'text-gray-400'">
              {{ remainingLabel(portion) }}
            </span>
          </div>
        </div>

        <p class="m-0 mt-3 text-xs text-gray-500">
          Total {{ draftTotal.toLocaleString() }}
          <span v-if="group.portions.length > 1">across {{ group.portions.length }} capacity types</span>
        </p>

        <div class="mt-4 flex justify-end gap-2">
          <button class="btn-secondary" type="button" @click="emit('close')">Cancel</button>
          <button class="btn-primary" type="button" :disabled="!canSave || saving" @click="save">Save</button>
        </div>
      </template>
    </div>

    <ConfirmDialog
      :open="pendingOverbook.length > 0"
      title="Overbook this week?"
      :message="overbookMessage"
      confirm-label="Overbook"
      @confirm="saveConfirmed"
      @cancel="pendingOverbook = []"
    />
  </v-dialog>
</template>

<script setup lang="ts">
  import { computed, reactive, ref, watch } from 'vue';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import { ASSIGNMENT_TYPE } from '@/types/capacityCalendar';
  import { changedPortions, overbookedPortions } from '@/utils/blockCapacityEdit';
  import type { OverbookedPortion, PortionEdit } from '@/utils/blockCapacityEdit';
  import { formatRemaining, groupLabel } from '@/utils/scheduleBoard';
  import type { ScheduleGroup, SchedulePortion } from '@/utils/scheduleBoard';

  const props = defineProps<{
    group: ScheduleGroup | null;
    weekLabel: string;
    saving?: boolean;
    // Remaining capacity for this week, per calendar row — the board reads it from the store,
    // so the dialog stays a pure editor over what it is handed.
    remainingByRowId: Record<string, number>;
  }>();
  const emit = defineEmits<{ close: []; save: [edits: PortionEdit[]] }>();

  const draft = reactive<Record<string, number>>({});
  const pendingOverbook = ref<OverbookedPortion[]>([]);

  // A reservation may carry a productCategory from the PO it references for tracking; that
  // describes the PO, not the block, and the backend allows a reservation a real quantity.
  const isNonCapacityPo = computed(() => Boolean(props.group?.productCategory) && props.group?.type !== ASSIGNMENT_TYPE.manual);

  const edits = computed(() => (props.group ? changedPortions(props.group.portions, draft) : []));
  const canSave = computed(() => edits.value.length > 0 && Object.values(draft).every((value) => Number.isFinite(value) && value >= 0));
  const draftTotal = computed(() => (props.group?.portions ?? []).reduce((total, portion) => total + (draft[portion.assignmentId] ?? portion.quantity), 0));

  const overbookMessage = computed(
    () => `This books past what the week has left:\n${pendingOverbook.value.map((entry) => `${entry.typeCode} over by ${entry.by.toLocaleString()}`).join('\n')}`
  );

  // Reseeded per open so a cancelled edit never leaks into the next block opened.
  watch(
    () => props.group,
    (group) => {
      Object.keys(draft).forEach((key) => delete draft[key]);
      (group?.portions ?? []).forEach((portion) => {
        draft[portion.assignmentId] = portion.quantity;
      });
      pendingOverbook.value = [];
    },
    { immediate: true }
  );

  function remainingLabel(portion: SchedulePortion): string {
    const remaining = props.remainingByRowId[portion.rowId];
    if (remaining === undefined) return '';
    return formatRemaining(remaining);
  }

  function isOverbooked(portion: SchedulePortion): boolean {
    return (props.remainingByRowId[portion.rowId] ?? 0) < 0;
  }

  function save(): void {
    if (!props.group) return;
    const over = overbookedPortions(props.group.portions, draft, props.remainingByRowId);
    if (over.length > 0) {
      pendingOverbook.value = over;
      return;
    }
    emit('save', edits.value);
  }

  function saveConfirmed(): void {
    pendingOverbook.value = [];
    emit('save', edits.value);
  }
</script>
